<template>
  <div>
    <mobialis-form-with-validation
      @onValidate="continueForm"
      v-av-analytics-form-submit="{ elementText: $t('message.common.continue') }"
    >
      <div :class="{ 'row justify-content-between': showBlocks }">
        <div :class="{ 'col-sm-7': showBlocks }">
          <ReviewYourResponsesHero v-if="isAVRDesktop || isAVREmbed"></ReviewYourResponsesHero>
          <div v-if="isAVRMobile" class="mb-4">
            <p>
              {{ $t('message.credit.reviewformCardText') }}
            </p>
            <hr />
            <div>
              <div class="row">
                <div class="col-5">
                  <img class="license" :src="require('@/assets/license.svg')" alt="License" />
                </div>
                <div class="col-7 pl-0 license-text">
                  <h5>{{ $t('message.credit.reviewformCardText2') }}</h5>
                </div>
              </div>
            </div>
            <hr />
          </div>

          <mobialis-text-input-editable
            :rules="{ required: true, regex: /^([^0-9]*)$/ }"
            :label="$t('message.common.firstName')"
            v-model="firstName"
            vid="firstName"
            data-cy="firstName"
            :aria-labels="textInputAriaLabels"
          ></mobialis-text-input-editable>
          <mobialis-text-input-editable
            :rules="{ required: true, regex: /^([^0-9]*)$/ }"
            :label="$t('message.common.lastName')"
            v-model="lastName"
            vid="lastName"
            data-cy="lastName"
            :aria-labels="textInputAriaLabels"
          ></mobialis-text-input-editable>
          <mobialis-text-input-editable
            rules="required|length:10"
            :label="$t('message.common.phoneNumber')"
            v-model="phoneNumber"
            vid="phoneNumber"
            placeholder="(   )"
            mask-type="phoneNumber"
            type="tel"
            data-cy="phoneNumber"
            :aria-labels="textInputAriaLabels"
          ></mobialis-text-input-editable>
          <mobialis-text-input-editable
            rules="required|email"
            :label="$t('message.common.emailAddress')"
            v-model="emailAddress"
            vid="emailAddress"
            data-cy="emailAddress"
            type="email"
            :aria-labels="textInputAriaLabels"
          ></mobialis-text-input-editable>
          <mobialis-text-input-editable
            :rules="birthDateValidationRule"
            ref="bd"
            :label="$t('message.credit.birthday')"
            v-model="birthdate"
            vid="birthdate"
            :helper="$t('message.credit.birthdayHelper')"
            mask-type="date"
            :raw="false"
            inputmode="numeric"
            data-cy="birthdate"
            :aria-labels="textInputAriaLabels"
          >
            <!-- TODO: custom mask for birthday? -->
          </mobialis-text-input-editable>
          <mobialis-text-input-editable
            rules="required"
            :label="$t('message.credit.streetAddress')"
            v-model="streetAddress"
            vid="streetaddress"
            data-cy="streetAddress"
            :aria-labels="textInputAriaLabels"
          ></mobialis-text-input-editable>
          <mobialis-text-input-editable
            :label="$t('message.credit.unit#')"
            v-model="unitNumber"
            vid="unitNumber"
            type="number"
            data-cy="unitNumber"
            :aria-labels="textInputAriaLabels"
          ></mobialis-text-input-editable>
          <mobialis-text-input-editable
            rules="required"
            :label="$t('message.common.city')"
            v-model="city"
            vid="city"
            data-cy="city"
            :aria-labels="textInputAriaLabels"
          ></mobialis-text-input-editable>
          <mobialis-selection-input-editable
            rules="required"
            :label="$t('message.common.province')"
            v-model="province"
            vid="province"
            :options="provinces"
            data-cy="province"
            :aria-labels="textInputAriaLabels"
          ></mobialis-selection-input-editable>
          <mobialis-text-input-editable
            rules="required|length:6"
            :label="$t('message.common.postalCode')"
            v-model="postalCode"
            vid="postalCode"
            mask-type="postalCode"
            data-cy="postalCode"
            :aria-labels="textInputAriaLabels"
          ></mobialis-text-input-editable>
        </div>
        <div v-if="showBlocks" class="col-sm-4 d-none d-sm-block">
          <MobialisCard class="mb-4">
            <img class="license mb-4" :src="require('@/assets/license.svg')" alt="License" />
            <p>
              {{ $t('message.credit.reviewformCardText') }}
            </p>
          </MobialisCard>
        </div>
      </div>
      <mobialis-button type="submit" class="mt-4 mb-1" data-cy="submitReviewForm">{{
        $t('message.common.continue')
      }}</mobialis-button>
      <p class="d-flex justify-content-center small my-0">
        {{ $t('message.credit.disclaimer.noAffectScore') }}
      </p>
    </mobialis-form-with-validation>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ReviewYourResponsesHero from '@/components/credit/hero/ReviewYourResponses.vue';
import log from '@/services/log';
import { provinceMixin, formMixin } from '@/mixins';
import { AVAnalyticsEventTriggers } from '@/utils/enums';
import helperFunctions from '@/utils/helperFunctions';

export default {
  name: 'ReviewForm',
  mixins: [provinceMixin, formMixin],
  beforeRouteEnter: helperFunctions.onlyAcceptRoutes(['creditform2']),
  data() {
    return {
      textInputAriaLabels: {
        edit: this.$t('message.common.edit'),
        save: this.$t('message.common.save'),
        cancel: this.$t('message.common.cancel'),
      },
      firstName: '',
      lastName: '',
      emailAddress: '',
      phoneNumber: '',
      birthdate: '',
      streetAddress: '',
      unitNumber: '',
      city: '',
      province: '',
      postalCode: '',
    };
  },
  components: {
    ReviewYourResponsesHero,
  },
  mounted() {
    this.firstName = this.$store.state.user.firstName;
    this.lastName = this.$store.state.user.lastName;
    this.emailAddress = this.$store.state.user.emailAddress;
    this.phoneNumber = this.$store.state.user.phoneNumber;
    this.birthdate = this.$store.state.user.birthdate;
    this.streetAddress = this.$store.state.user.streetAddress;
    this.unitNumber = this.$store.state.user.unit;
    this.city = this.$store.state.user.city;
    this.province = this.$store.state.user.province;
    this.postalCode = this.$store.state.user.postalCode;
  },
  methods: {
    ...mapActions(['credit/getQuestions', 'credit/setQuestions']),
    async continueForm(isValid) {
      if (!isValid) {
        return;
      }
      try {
        this.$store.commit('SET_LOADING', true);
        this.$store.commit('user/SET_FIRST_NAME', this.firstName);
        this.$store.commit('user/SET_LAST_NAME', this.lastName);
        this.$store.commit('user/SET_EMAIL_ADDRESS', this.emailAddress);
        this.$store.commit('user/SET_PHONE_NUMBER', this.phoneNumber);
        this.$store.commit('user/SET_BIRTHDATE', this.birthdate);
        this.$store.commit('user/SET_STREET_ADDRESS', this.streetAddress);
        this.$store.commit('user/SET_UNIT', this.unitNumber);
        this.$store.commit('user/SET_CITY', this.city);
        this.$store.commit('user/SET_PROVINCE_CODE', this.getProvinceCode(this.province));
        this.$store.commit('user/SET_PROVINCE', this.province);
        this.$store.commit('user/SET_POSTAL_CODE', this.postalCode);
        const response = await this['credit/getQuestions']();
        this['credit/setQuestions'](response.data);
        this.$router.push('confirm-id');
      } catch (error) {
        // we only log when it's an actual error from the server or kijiji for debugging
        if ((error.response && error.response.status === 500) || this.isKijijiDealer) {
          log('Error in Credit Review Form', error);
        }
        // if it's not US, try to get regional credit score
        if (!this.getThisIsAmerica) {
          try {
            // this will send off their postal to the RCC and get a credit score for their postal code.
            await this.$store.dispatch('credit/getRegionalCreditScore');
          } catch (err) {
            log('Error in Credit Review form, RCC failed', err);
          }
        }
        // Go to the results page, will display error
        // message if we don't have a credit score
        if (this.prequalificationIncomeAndDebtEnabled) {
          this.$router.push({
            name: `income-and-mortgage-${this.baseRoute}`,
          });
        } else {
          this.$router.push({
            name: `credit-results-${this.baseRoute}`,
          });
        }
      } finally {
        // We update the lead info whether it succeed or not
        try {
          await this.$store.dispatch('lead/updateLead');
        } catch (err) {
          // Credit flow is complete, trigger the flow complete analytics event
          helperFunctions.sendAVAnalyticsEvent(this.$route.path, AVAnalyticsEventTriggers.FORM_SUBMIT, this.$store);
          log('Error in Credit Review Form - Update Lead: ', err);
          this.$router.push({
            name: `credit-in-error-${this.baseRoute}`,
          });
        } finally {
          this.$store.commit('SET_LOADING', false);
        }
      }
    },
  },
  computed: {
    ...mapGetters('business', ['getThisIsAmerica', 'isKijijiDealer', 'prequalificationIncomeAndDebtEnabled']),
  },
};
</script>

<style lang="scss" scoped>
.license {
  max-width: 100%;
}
.license-text {
  display: flex;
  justify-content: center;
  align-items: center;
  color: #4b5863;
}
</style>
